import api from '@/api'
import i18n from '@/setup/i18n'

const state = {
  schedules: [],
  currentSchedule: null,
  notifications: null,
  saving: false
}

const mutations = {
  SET_SCHEDULES(state, payload) {
    state.schedules = payload
  },

  SET_CURRENT_SCHEDULE(state, payload) {
    state.currentSchedule = payload
  },

  SET_NOTIFICATIONS(state, payload) {
    state.notifications = payload
  },

  SET_SAVING(state, payload) {
    state.saving = payload
  }
}

/**
 * Every schedule mutation returns the full object from the backend, so we replace currentSchedule
 * with the response instead of patching it locally. That way the view can never drift away from the
 * server's resolution - and it is the server that decides what the screen actually shows.
 */
const actions = {
  fetchSchedules({ commit, rootState }) {
    const workspace = rootState.workspaces.currentWorkspace
    if (!workspace) return Promise.resolve()

    commit('appshell/startSectionLoading', null, { root: true })

    return api.schedules
      .getSchedules(workspace.id)
      .then((response) => {
        commit('SET_SCHEDULES', response.data)
        commit('appshell/endSectionLoading', null, { root: true })
      })
      .catch((error) => {
        console.error('fetchSchedules', error)
        commit('appshell/endSectionLoading', null, { root: true })
        commit('appshell/setDataFetchingState', true, { root: true })
      })
  },

  fetchSchedule({ commit }, scheduleId) {
    commit('appshell/startSectionLoading', null, { root: true })

    return api.schedules
      .getSchedule(scheduleId)
      .then((response) => {
        commit('SET_CURRENT_SCHEDULE', response.data)
        commit('appshell/endSectionLoading', null, { root: true })
        return response.data
      })
      .catch((error) => {
        console.error('fetchSchedule', error)
        commit('appshell/endSectionLoading', null, { root: true })
        commit('appshell/setDataFetchingState', true, { root: true })
        throw error
      })
  },

  createSchedule({ commit, dispatch, rootState }, payload) {
    const workspace = rootState.workspaces.currentWorkspace
    if (!workspace) return Promise.reject(new Error('No workspace selected'))

    commit('SET_SAVING', true)

    return api.schedules
      .createSchedule(workspace.id, payload)
      .then((response) => {
        commit('SET_SAVING', false)
        commit('SET_NOTIFICATIONS', { type: 'success', message: i18n.t('scheduler.messages.created') })
        dispatch('fetchSchedules')
        return response.data
      })
      .catch((error) => {
        commit('SET_SAVING', false)
        commit('SET_NOTIFICATIONS', { type: 'error', message: errorMessage(error) })
        throw error
      })
  },

  updateSchedule({ commit }, { scheduleId, data }) {
    commit('SET_SAVING', true)

    return api.schedules
      .updateSchedule(scheduleId, data)
      .then((response) => {
        commit('SET_CURRENT_SCHEDULE', response.data)
        commit('SET_SAVING', false)
        commit('SET_NOTIFICATIONS', { type: 'success', message: i18n.t('scheduler.messages.saved') })
        return response.data
      })
      .catch((error) => {
        commit('SET_SAVING', false)
        commit('SET_NOTIFICATIONS', { type: 'error', message: errorMessage(error) })
        throw error
      })
  },

  removeSchedule({ commit, dispatch }, scheduleId) {
    return api.schedules
      .removeSchedule(scheduleId)
      .then(() => {
        commit('SET_NOTIFICATIONS', { type: 'success', message: i18n.t('scheduler.messages.removed') })
        dispatch('fetchSchedules')
      })
      .catch((error) => {
        commit('SET_NOTIFICATIONS', { type: 'error', message: errorMessage(error) })
        throw error
      })
  },

  addWindow({ commit }, { scheduleId, data }) {
    return mutateSchedule(commit, api.schedules.addWindow(scheduleId, data), 'scheduler.messages.windowAdded')
  },

  updateWindow({ commit }, { scheduleId, windowId, data }) {
    return mutateSchedule(commit, api.schedules.updateWindow(scheduleId, windowId, data), 'scheduler.messages.windowSaved')
  },

  removeWindow({ commit }, { scheduleId, windowId }) {
    return mutateSchedule(commit, api.schedules.removeWindow(scheduleId, windowId), 'scheduler.messages.windowRemoved')
  },

  addException({ commit }, { scheduleId, data }) {
    return mutateSchedule(commit, api.schedules.addException(scheduleId, data), 'scheduler.messages.exceptionAdded')
  },

  removeException({ commit }, { scheduleId, exceptionId }) {
    return mutateSchedule(commit, api.schedules.removeException(scheduleId, exceptionId), 'scheduler.messages.exceptionRemoved')
  },

  setDevices({ commit }, { scheduleId, macs }) {
    return mutateSchedule(commit, api.schedules.setDevices(scheduleId, macs), 'scheduler.messages.devicesSaved')
  }
}

/**
 * The backend returns a business message in the body (e.g. window collision, screen already in another
 * schedule). We surface it as is - it is written for the user, not for the log.
 */
function errorMessage(error) {
  const message = error && error.response && error.response.data && error.response.data.message
  return message || i18n.t('scheduler.messages.genericError')
}

function mutateSchedule(commit, request, successKey) {
  commit('SET_SAVING', true)

  return request
    .then((response) => {
      commit('SET_CURRENT_SCHEDULE', response.data)
      commit('SET_SAVING', false)
      commit('SET_NOTIFICATIONS', { type: 'success', message: i18n.t(successKey) })
      return response.data
    })
    .catch((error) => {
      commit('SET_SAVING', false)
      commit('SET_NOTIFICATIONS', { type: 'error', message: errorMessage(error) })
      throw error
    })
}

const getters = {
  schedules: (state) => state.schedules,
  currentSchedule: (state) => state.currentSchedule,
  notifications: (state) => state.notifications,
  saving: (state) => state.saving
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
